import io from 'socket.io-client';

export default class Connector {

    constructor(options){
        this._socket = null;
        this._notificationHandler = null;
        this._initialized = false;
        this._connectedToService = false;
        this._isReconnecting = false;
        this._clientInfo = {};

        this._options = {
            notificationHandler: null,
            serviceId: '',
            serviceUrl: '',
            socketIoPath: '',
            defaultParticipantName: 'Guest'
        };

        this._socketUrl = '';
        this._socketId = '';

        this._initData = null;
        this._sessionMap = {};
        this._tempMeetingId = '';
        this._originJoinMeetingArguments = null;
        this._injectedParticipant = null;

        try {
            let adialiveSessionMap = window.sessionStorage.getItem('al5-session-map');
            if (adialiveSessionMap){
                this._sessionMap = JSON.parse(adialiveSessionMap);
            }
        } catch(e){
            this._sessionMap = {};
        }

        for(let props in options){
            if (this._options.hasOwnProperty(props)){
                switch(props){
                    case 'notificationHandler':
                        this.setNotificationHandler(options[props]);
                        break;
                    case 'serviceUrl':
                        this._socketUrl = options.serviceUrl + '/al5/'+options.serviceId;
                    default:
                        this._options[props] = options[props];
                        break;
                }
            }
        }

        if (!this._options.serviceUrl || !this._options.serviceId) {
            throw new Error('serviceUrl and/or serviceId not set');
            return;
        }

        this._conArgs = {adiaRecon:0, adiaOldId:'none', adiaOldSession:'none', adiaConVer:'5.0.0'};

        let _this = this;
        this._raiseNotification = (notificationObj) => {
            switch(notificationObj.type){
                case 'sessionRestored':
                    _this._isReconnecting = false;
                    break;

                case 'joinedMeeting':
                    // map meetingId to participantId
                    if (!_this._clientInfo.isAdviser && _this._tempMeetingId === notificationObj.data.meetingInfo._id){
                        _this._sessionMap[notificationObj.data.meetingInfo._id] = notificationObj.data.participantInfo._id;
                        try{
							window.sessionStorage.setItem('al5-session-map', JSON.stringify(_this._sessionMap));
						} catch(e){}
                    }
                    _this._originJoinMeetingArguments = null;
                    break;

                case 'meetingInfo':
                    if (!notificationObj.data.participantInfo) {
                        let participantId = _this._sessionMap[notificationObj.data.meetingInfo._id];
                        if (participantId) {
                            let filteredParticipants = notificationObj.data.meetingInfo.participants.filter(participant => participant._id === participantId);
                            if (filteredParticipants.length > 0) {
                                notificationObj.data.participantInfo = filteredParticipants[0];
                                _this._injectedParticipant = notificationObj.data.participantInfo;
                            }
                        }
                    }
                    break;

                case 'clientInfoUpdate':
                    _this._clientInfo = notificationObj.data;
                    break;

                case 'serviceError':
                    if (_this._originJoinMeetingArguments && notificationObj.data.error.status === 404){
                        delete _this._sessionMap[_this._originJoinMeetingArguments.meetingId];

                        try {
                            window.sessionStorage.setItem('al5-session-map', JSON.stringify(_this._sessionMap));
                        } catch(e){}
                        return _this.joinMeeting(_this._originJoinMeetingArguments.meetingId, {name:_this._originJoinMeetingArguments.participantInfo.name,email:_this._originJoinMeetingArguments.participantInfo.email}, _this._originJoinMeetingArguments.deviceInfo, _this._originJoinMeetingArguments.standaloneMode); // silently fail and try again ;)..
                    }
                    break;
            }
            if (_this._options.notificationHandler){
                setTimeout(() => {
                    _this._options.notificationHandler(notificationObj);
                }, 0);
            }
        };

        this._raiseError = (context, errorNo, errorMsg) => {
            _this._raiseNotification({
                type: 'serviceError',
                data: {
                    context: context,
                    error: {
                        errorNo: errorNo,
                        status: Math.floor(errorNo / 100),
                        message: errorMsg
                    }
                }
            });
        };
    }

    init() {
        if (!this._initialized){
            this._initialized = true;

            if (this._options.socketIoPath){
                this._socket = io(this._socketUrl, {query:this._conArgs, forceNew:true, path: this._options.socketIoPath});
            } else {
                this._socket = io(this._socketUrl, {query:this._conArgs, forceNew:true});
            }

            let _this = this;

            this._socket.on('connect', function(){
                _this._socketId = _this._socket.io.engine.id;
                _this._conArgs.adiaRecon = 1;
                _this._conArgs.adiaOldId = _this._socketId;
            });

            this._socket.on('error', (data) => {
                _this._raiseError('socketIo', 50004, 'socket io error');
            });

            this._socket.on('reconnecting',() => {
                if (!_this._isReconnecting){
                    _this._isReconnecting = true;
                    _this._raiseNotification({
                        type:'reconnecting',
                        data: null
                    });
                }
            });

            this._socket.on('notifications', (n) =>{
                let i;
                let cNot;
                for (i = 0; i < n.length; i++){
                    cNot = n[i];
                    _this._raiseNotification(cNot);
                }
            });

            this._socket.on('initialize', (data) => {
                _this._isReconnecting = false;
                _this._initData = data;
                _this._conArgs.adiaOldSession = data.clientInfo.id;

                if (!_this._connectedToService){
                    _this._connectedToService = true;
                    for(let i = 0; i < _this._initData.serviceEvents.length; i++){
                        (function(){
                            let serviceEvent = _this._initData.serviceEvents[i];
                            _this._socket.on(serviceEvent, (data) => {
                                _this._raiseNotification({
                                    type: serviceEvent,
                                    data: data
                                })
                            });
                        })();
                    }
                }
                _this._initData.serviceEvents.push('reconnecting');

                _this._raiseNotification({type:'initialize', data: data});
                _this._raiseNotification({type:'clientInfoUpdate', data: data.clientInfo});
            });
        }
    }

    setNotificationHandler(handler){
        if(typeof handler === 'function'){
            this._options.notificationHandler = handler;
        }
    }

    joinMeetingStandalone(meetingId){
        this.joinMeeting(meetingId, undefined, undefined, true);
    }

    joinMeeting(meetingId, alternativeParticipantInfo, deviceInfo, standaloneMode, recordingConfirmed){
        if (typeof alternativeParticipantInfo === 'string'){
            alternativeParticipantInfo = {
                name: alternativeParticipantInfo,
                email:''
            };
        } else if (!alternativeParticipantInfo && !this._clientInfo.isAdviser){
            alternativeParticipantInfo = {
                name: '',
                email:''
            };
        } else if (alternativeParticipantInfo && typeof alternativeParticipantInfo === 'object'){
            alternativeParticipantInfo = {
                name: alternativeParticipantInfo.name?alternativeParticipantInfo.name:'',
                email:alternativeParticipantInfo.email?alternativeParticipantInfo.email:''
            };
        }
        if (!this._clientInfo.isAdviser && this._sessionMap[meetingId]){
            this._originJoinMeetingArguments = {
                meetingId: meetingId,
                participantInfo: {
                    name: alternativeParticipantInfo.name,
                    email: alternativeParticipantInfo.email
                },
                deviceInfo: deviceInfo,
                standaloneMode: standaloneMode
            };
            meetingId = this._sessionMap[meetingId];
            if (this._injectedParticipant && this._injectedParticipant._id === meetingId){
                if ((alternativeParticipantInfo.name && this._injectedParticipant.name !== alternativeParticipantInfo.name) || (alternativeParticipantInfo.email && this._injectedParticipant.email !== alternativeParticipantInfo.email)){
                    meetingId = this._originJoinMeetingArguments.meetingId;
                    this._originJoinMeetingArguments = null;
                }
            }
        } else {
            this._originJoinMeetingArguments = null;
        }
        this._tempMeetingId = meetingId;
        if (this._socket){
            if (alternativeParticipantInfo && !alternativeParticipantInfo.name){
                alternativeParticipantInfo.name = this._options.defaultParticipantName;
            }
            if (deviceInfo){
                this._socket.emit('joinMeeting', {
                    meetingId: meetingId,
                    participantInfo: alternativeParticipantInfo,
                    deviceInfo: deviceInfo,
                    standaloneMode: standaloneMode,
                    recordingConfirmed: !!recordingConfirmed
                });
            } else {
                this._socket.emit('joinMeeting', {
                    meetingId: meetingId,
                    participantInfo: alternativeParticipantInfo,
                    standaloneMode: standaloneMode,
                    recordingConfirmed: !!recordingConfirmed
                });
            }
        }
    }

    setDeviceUsage(clientId, deviceInfo){
        if (this._socket){
            this._socket.emit('setDeviceUsage', {
                clientId: clientId,
                deviceInfo: deviceInfo
            });
        }
    }

    updateDeviceInfo(deviceInfo){
        if (this._socket){
            this._socket.emit('updateDeviceInfo', deviceInfo);
        }
    }

    leaveMeeting(){
        if (this._socket){
            this._socket.emit('leaveMeeting');
        }
    }

    getMeetingInfo(meetingId){
        if (this._socket) {
            this._injectedParticipant = null;
            this._socket.emit('getMeetingInfo', meetingId);
        }
    }

	setCurrentApp(app){
        if (this._socket) {
            this._socket.emit('setCurrentApp', app+'');
        }
    }

    setCurrentActiveUser(clientId){
        if (this._socket) {
            this._socket.emit('setCurrentActiveUser', clientId);
        }
    }

    unsetCurrentActiveUser(){
        if (this._socket) {
            this._socket.emit('setCurrentActiveUser', null);
        }
    }

    setEditPermissions(clientId, permissions){
        if (this._socket) {
            this._socket.emit('setEditPermissions', {
                clientId: clientId,
                permissions: permissions
            });
        }
    }

    requestSpeakerPermission(isRequesting) {
        if (this._socket) {
            this._socket.emit('requestSpeakerPermission', isRequesting);
        }
    }

    setSpeakerPermission(clientId, permission) {
        if (this._socket) {
            this._socket.emit('setSpeakerPermission', {
                clientId: clientId,
                permission: permission
            });
        }
    }

    adviserAuth(authToken){
        if (this._socket){
            this._socket.emit('adviserAuth', authToken);
        }
    }

    startRecording(){
        if (this._socket){
            this._socket.emit('setRecording', true);
        }
    }

    stopRecording(){
        if (this._socket){
            this._socket.emit('setRecording', false);
        }
    }

    logout(){
        if (this._socket){
            this._socket.emit('logout');
        }
    }

    kickUser(clientId) {
        if (this._socket){
            this._socket.emit('kickUser', clientId);
        }
    }

    completeMeeting() {
        if (this._socket){
            this._socket.emit('completeMeeting');
        }
    }

    confirmRecording(confirmed) {
        if (this._socket){
            this._socket.emit('confirmRecording', confirmed);
        }
    }

    close(){
        if(this._socket){
            this._socket.close();
        }
    }
}
